import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { categoryVideos, categoryInfo } from '@/mocks/contentData';
import { useState, useMemo } from 'react';

export default function Category() {
  const { slug = 'mingxing-bagua' } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'hot'>('latest');

  const info = categoryInfo[slug as keyof typeof categoryInfo];
  const videos = categoryVideos[slug as keyof typeof categoryVideos] || [];

  const sortedVideos = useMemo(() => {
    const arr = [...videos];
    if (sortBy === 'latest') {
      return arr.sort((a, b) => b.publishTime.localeCompare(a.publishTime));
    }
    return arr.sort((a, b) => {
      const aNum = parseInt(a.views.replace(/[^0-9]/g, ''));
      const bNum = parseInt(b.views.replace(/[^0-9]/g, ''));
      return bNum - aNum;
    });
  }, [videos, sortBy]);

  if (!info) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-foreground-500">分类不存在</p>
      </div>
    );
  }

  return (
    <main className="pt-16">
      {/* Category header */}
      <section className="relative py-16 md:py-20 bg-background-100">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500/20">
                  <i className={`${info.icon} text-primary-400 text-2xl`}></i>
                </div>
                <span className="px-3 py-1 text-xs font-semibold rounded-full border border-primary-500/40 text-primary-400">
                  {info.count.toLocaleString()} 条视频
                </span>
              </div>
              <h1 className="text-[clamp(1.8rem,3vw,3rem)] font-black text-foreground-50 tracking-[-0.02em] mb-3">
                {info.name}
              </h1>
              <p className="text-base text-foreground-500 max-w-xl leading-relaxed">
                {info.description}
              </p>
            </div>
            {/* Sort tabs */}
            <div className="flex items-center gap-1 bg-background-50 rounded-full p-1 border border-background-200/70">
              <button
                onClick={() => setSortBy('latest')}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  sortBy === 'latest'
                    ? 'bg-primary-500 text-background-950'
                    : 'text-foreground-500 hover:text-foreground-300'
                }`}
              >
                最新更新
              </button>
              <button
                onClick={() => setSortBy('hot')}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  sortBy === 'hot'
                    ? 'bg-primary-500 text-background-950'
                    : 'text-foreground-500 hover:text-foreground-300'
                }`}
              >
                最热排行
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Video grid */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {sortedVideos.map((video) => (
              <CategoryVideoCard key={video.id} video={video} />
            ))}
          </div>

          {sortedVideos.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                <i className="ri-inbox-line text-foreground-600 text-2xl"></i>
              </div>
              <p className="text-foreground-600">暂无视频内容</p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

function CategoryVideoCard({ video }: { video: typeof categoryVideos['mingxing-bagua'][0] }) {
  return (
    <Link
      to={`/video/${video.id}`}
      className="group block rounded-2xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-500/40 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={video.coverImage}
          alt={video.title}
          title={`黑料视频 - ${video.title}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50/60 to-transparent" />
        <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-background-950/80 text-foreground-200 text-[11px] font-medium rounded-md whitespace-nowrap">
          {video.duration}
        </span>
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          {video.isHot && (
            <span className="px-2 py-0.5 bg-primary-500 text-background-950 text-[10px] font-bold rounded-full whitespace-nowrap">
              HOT
            </span>
          )}
          {video.isNew && (
            <span className="px-2 py-0.5 bg-accent-500 text-background-950 text-[10px] font-bold rounded-full whitespace-nowrap">
              NEW
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground-100 mb-2 group-hover:text-primary-400 transition-colors line-clamp-2 leading-snug">
          {video.title}
        </h3>
        <p className="text-xs text-foreground-600 mb-3 line-clamp-1">{video.excerpt}</p>
        <div className="flex items-center gap-3 text-xs text-foreground-600">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-eye-line"></i>
            </span>
            {video.views}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-chat-3-line"></i>
            </span>
            {video.comments.toLocaleString()}
          </span>
          <span className="ml-auto text-foreground-700">{video.publishTime}</span>
        </div>
      </div>
    </Link>
  );
}
